import { wildResponseHandler } from "@wildidea/tools/utils/wildResponseHandler.js";

import { IGroupsServiceClient } from "../../../../../src/auth/domain/IGroupsService.js";
import {
  API_PREFIX,
  CreateGroupInput,
  GroupMemberInput,
  GroupSummary,
  ResolvedInput,
  UserSummary,
  WithGroupId,
  WithMemberRole,
} from "../model.js";

const JSON_RESPONSE_HEADERS = { Accept: "application/json" };
const JSON_REQUEST_HEADERS = { "Content-Type": "application/json" };

export interface GroupsServiceClientConfig {
  baseUri?: string;
}

export class GroupsServiceClient implements IGroupsServiceClient {
  private readonly baseUri: string;
  protected baseHeaders: Record<string, string> = JSON_RESPONSE_HEADERS;
  constructor(
    /**
     * A string to prepend to every API call, e.g. `https://dev.onemap.earth`.
     *
     * Not necessary if you are running in a web app. The calls will be made to a relative path
     * from `/api/` in the current domain, which should map directly to the same the API paths in
     * API Gateway.
     */
    { baseUri = "" }: GroupsServiceClientConfig,
  ) {
    this.baseUri = baseUri;
  }

  async createGroup(input: CreateGroupInput): Promise<GroupSummary> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/groups`, {
        method: "POST",
        headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
        body: JSON.stringify(input),
      }),
      "createGroup",
    );
  }

  async createJoinRequest({
    groupId,
    userId,
  }: GroupMemberInput): Promise<void> {
    await wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/groups/${groupId}/joins/${userId}`, {
        method: "POST",
        headers: this.baseHeaders,
      }),
      "createJoinRequest",
    );
  }

  async resolveJoinRequest({
    groupId,
    userId,
    ...body
  }: GroupMemberInput & ResolvedInput): Promise<void> {
    await wildResponseHandler(
      fetch(
        `${this.baseUri}${API_PREFIX}/groups/${groupId}/joins/${userId}/resolve`,
        {
          method: "POST",
          headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
          body: JSON.stringify(body),
        },
      ),
      "resolveJoinRequest",
    );
  }

  async getPendingJoinRequests({
    groupId,
  }: WithGroupId): Promise<UserSummary[]> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/groups/${groupId}/joins`, {
        headers: this.baseHeaders,
      }),
      "getPendingJoinRequests",
    );
  }

  async updateMemberRole({
    groupId,
    userId,
    ...body
  }: GroupMemberInput & WithMemberRole): Promise<void> {
    await wildResponseHandler(
      fetch(
        `${this.baseUri}${API_PREFIX}/groups/${groupId}/users/${userId}/update`,
        {
          method: "POST",
          headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
          body: JSON.stringify(body),
        },
      ),
      "updateMemberRole",
    );
  }

  async removeUserFromGroup({
    userId,
    groupId,
  }: GroupMemberInput): Promise<void> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/groups/${groupId}/users/${userId}`, {
        method: "DELETE",
        headers: this.baseHeaders,
      }),
      "removeUserFromGroup",
    );
  }

  async getGroupUsers(input: WithGroupId): Promise<UserSummary[]> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/groups/${input.groupId}/users`, {
        headers: this.baseHeaders,
      }),
      "getGroupUsers",
    );
  }
}
